import { React, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css';

// components
import Loader from "../../components/loader";
import Logo from "../../components/logo";
import SearchResultsSection from "../../components/search-results";
import Modal from "../../components/modal";
import BackButton from "../../components/back-button";

// hooks
import useFetchHandler from "../../hooks/use-fetch-handler";

export default function PatientCreatePage() {
    const navigate = useNavigate()

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [gender, setGender] = useState("");
    const [dob, setDOB] = useState("");

    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [isCalendarOpen, setIsCalendarOpen] = useState(false)

    const [patients, setPatients] = useState([])
    const [showPatientResultsSection, setShowPatientResultsSection] = useState(false)

    const {
        loading,
        error,
        responseSuccess,
        handleFetch,
        resetRequestState
    } = useFetchHandler();

    const {
        loading: loadingPatients,
        error: loadingPatientsError,
        handleFetch: handleFetchPatients,
        resetRequestState: resetPatientSearchRequestState
    } = useFetchHandler();

    const fetchPatients = useCallback(async () => {
        await handleFetchPatients(
            "POST",
            "patient/search",
            JSON.stringify({ staff_id: localStorage.getItem("staffId") }),
            true,
            (data) => {
                setPatients(data?.patients)
                setShowPatientResultsSection(true);
            }
        )
    }, [handleFetchPatients])


    const registerPatient = useCallback(async () => {
        await handleFetch(
            "POST",
            "patient/register",
            JSON.stringify({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                gender: gender,
                dob: moment(dob).format("YYYY-MM-DD"),
                age: moment().diff(moment(dob, "DD MMM YYYY"), "years"),
                phone: phone.trim(),
                email: email.trim().toLowerCase(),
                staff_id: localStorage.getItem("staffId"),
            }),
            true,
            () => {
                fetchPatients()
            }
        )
    }, [dob, email, fetchPatients, firstName, gender, handleFetch, lastName, phone]);

    const handlePrevNavigation = useCallback(() => {
        if (showPatientResultsSection) {
            setShowPatientResultsSection(false)
            resetPatientSearchRequestState()
            resetRequestState()
            window.scrollTo(0, 0);
            return;
        }

        navigate(-1)
        window.scrollTo(0, 0);
    }, [navigate, resetPatientSearchRequestState, resetRequestState, showPatientResultsSection])

    if (loading || loadingPatients) {
        return <Loader message={loadingPatients ? "Loading clients, " : "Creating client, "} />
    }

    return (
        <>
            <main
                className={`relative flex-grow flex flex-col items-center justify-center p-10 gap-20`}
            >
                <BackButton handlePrevNavigation={handlePrevNavigation} />
                <Logo />
                {showPatientResultsSection ? (
                    <>
                        {loadingPatientsError && <p className="mt-4 text-center text-red-600 text-lg font-bold">{loadingPatientsError}</p>}
                        <SearchResultsSection searchResults={patients} />
                    </>
                ) : (
                    <form
                        className="w-full flex flex-col gap-5"
                        onSubmit={(event) => {
                            event.preventDefault();
                            registerPatient();
                        }}
                    >
                        {responseSuccess && <p className="mt-4 text-center text-green-500 text-lg font-bold">
                            Client created successfully!
                        </p>}
                        {loadingPatientsError && <p className="mt-4 text-center text-red-600 text-lg font-bold">Failed to load client list: {loadingPatientsError}</p>}
                        {error && <p className="mt-4 text-center text-red-600 text-lg font-bold">{error}</p>}
                        <h1 className="text-center text-xl font-semibold mb-5">
                            Create a new client
                        </h1>
                        <input
                            type="text"
                            className="custom-input px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none text-[#3E6975]"
                            value={firstName}
                            required
                            placeholder="First name"
                            onChange={(event) => {
                                setFirstName(event.target.value);
                            }}
                        />
                        <input
                            type="text"
                            className="custom-input px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none text-[#3E6975]"
                            value={lastName}
                            required
                            placeholder="Last name"
                            onChange={(event) => {
                                setLastName(event.target.value);
                            }}
                        />
                        <select
                            className={`
                        ${!gender ? 'text-[#3E6975]/50' : 'text-[#3E6975]'} w-full custom-select px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none
                        overflow-hidden`}

                            placeholder={'Select gender'}
                            name="gender"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            required
                        >
                            <option className="w-full text-base text-gray-400" value="" disabled selected>
                                Gender
                            </option>
                            <option className="w-full text-base" value={"M"}>
                                Male
                            </option>
                            <option className="w-full text-base" value={"F"}>
                                Female
                            </option>
                            <option className="w-full text-base" value={"O"}>
                                Other
                            </option>
                        </select>
                        <div className="flex flex-col w-full">
                            <input
                                type="text"
                                placeholder="Date of birth"
                                className="custom-input px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none text-[#3E6975] !w-full"
                                readOnly
                                required
                                value={dob}
                                onClick={() => setIsCalendarOpen(true)}
                            />
                        </div>
                        <input
                            type="tel"
                            className="custom-input px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none text-[#3E6975]"
                            value={phone}
                            required
                            placeholder="Phone"
                            onChange={(event) => {
                                setPhone(event.target.value);
                            }}
                        />
                        <input
                            type="email"
                            className="custom-input px-6 py-3 bg-text-1 rounded-lg text-xl font-normal focus:outline-none text-[#3E6975]"
                            value={email}
                            onChange={(event) => {
                                setEmail(event.target.value);
                            }}
                            required
                            placeholder="Email"
                        />
                        <input
                            className="mt-5 px-8 py-3 bg-[#f4831f] font-semibold rounded-[5px] text-white cursor-pointer capitalize focus:outline-none"
                            type="submit"
                            value={'Create client'}
                        />
                    </form>
                )}
            </main>
            <Modal
                open={isCalendarOpen}
                onClose={() => setIsCalendarOpen(false)}
                className={'!rounded-2xl p-2 !bg-background-1'}
                children={
                    <div className="flex flex-col gap-2">
                        <h6 className="p-2 text-text-1 text-base">Selected Date: {dob || "NA"}</h6>
                        <Calendar
                            className={'p-2 rounded text-base !bg-background-1 !text-text-1 !border-none !outline-none'}
                            onChange={(selectedDate) => {
                                const formattedDate = selectedDate.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                })
                                setDOB(formattedDate);
                            }}
                            calendarType="gregory"
                            defaultActiveStartDate={new Date('1980-01-01')}
                            tileClassName={'!p-3.5 !text-white !bg-transparent'}
                            defaultValue={dob ? new Date(dob) : undefined}
                            
                        />
                        <div className="flex items-center justify-end p-2">
                            <p
                                className={
                                    Boolean(dob) ? "cursor-pointer text-[#f4831f]" : "cursor-not-allowed text-[#f4831f]/30"
                                }
                                onClick={() => setIsCalendarOpen(false)}
                            >
                                Ok
                            </p>
                        </div>
                    </div>

                }
            />
        </>
    );
}
